<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">{{ seriesName }}</h3>
            </div>
            <div class="content-header-right col-md-6 col-12">
                <button class="btn btn-warning float-right" :disabled="marking" @click="markSeriesUsed">
                    Mark Series As Used
                    <i v-if="marking" class="fa fa-spinner fa-spin"></i>
                </button>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
                    <h4 class="mb-0">Gift Cards in Series</h4>
                    <div class="d-flex flex-wrap align-items-center">
                        <download-excel
                            ref="exporter-csv"
                            :data="exportCards"
                            :export-fields="exportFields"
                            type="csv"
                            style="display: none"
                        />
                        <download-excel
                            ref="exporter-xls"
                            :data="exportCards"
                            :export-fields="exportFields"
                            type="xls"
                            style="display: none"
                        />
                        <a href="#" class="export-link mr-2" @click.prevent="exportData('csv')">
                            <i class="fas fa-file-csv"></i> Export to CSV
                        </a>
                        <span class="mr-2">|</span>
                        <a href="#" class="export-link text-warning mr-3" @click.prevent="exportData('xls')">
                            <i class="fa fa-file-excel-o"></i> Export to Excel
                        </a>
                        <a
                            class="btn btn-info btn-sm"
                            :href="route('admin.pgc-giftcards.export-zip', seriesSlug)"
                        >
                            Zip PDF Files
                        </a>
                    </div>
                </div>

                <div class="card-body">
                    <form>
                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-group">
                                    <label for="card-search">Search by code</label>
                                    <input
                                        id="card-search"
                                        v-model="search"
                                        class="form-control form-control-sm"
                                        type="text"
                                        placeholder="PGC code"
                                    >
                                </div>
                            </div>
                            <div class="col-md-12 mb-2">
                                <button type="submit" class="btn btn-primary glow mb-1 mb-sm-0" @click.prevent="applySearch">
                                    Search
                                </button>
                                <button type="reset" class="btn btn-warning glow mb-1 mb-sm-0" @click.prevent="resetFilters">
                                    Reset
                                </button>
                            </div>
                        </div>
                    </form>

                    <vue-good-table
                        class="dataTable"
                        mode="remote"
                        :isLoading.sync="tableDataLoading"
                        :totalRows="totalRecords"
                        :pagination-options="paginationOptions"
                        :sort-options="sortingOptions"
                        @on-page-change="this.onPageChange"
                        @on-per-page-change="this.onPerPageChange"
                        styleClass="vgt-table"
                        :columns="columns"
                        :rows="allGiftCards"
                    >
                        <template slot="table-row" slot-scope="props">
                            <span v-if="props.column.field === 'sn'">
                                {{ serialNo(props) }}
                            </span>
                            <span v-else-if="props.column.field === 'amount'">
                                ₦{{ Number(props.row.amount).toLocaleString() }}
                            </span>
                            <span v-else-if="props.column.field === 'used_amount'">
                                ₦{{ Number(props.row.used_amount).toLocaleString() }}
                            </span>
                            <span v-else-if="props.column.field === 'remaining'">
                                ₦{{ remaining(props.row).toLocaleString() }}
                            </span>
                            <span v-else-if="props.column.field === 'status'">
                                <span v-if="props.row.fully_used_at" class="badge badge-danger">Fully Used</span>
                                <span v-else-if="props.row.is_used" class="badge badge-warning">Partially Used</span>
                                <span v-else class="badge badge-info">Unused</span>
                            </span>
                            <span v-else-if="props.column.field === 'is_active'">
                                <span v-if="props.row.is_active" class="badge badge-success">Active</span>
                                <span v-else class="badge badge-secondary">Inactive</span>
                            </span>
                            <span v-else-if="props.column.field === 'expires_at'">
                                {{ formatExpiryDateTime(props.row.expires_at) }}
                            </span>
                            <span v-else-if="props.column.field === 'created_at'">
                                {{ formatDateTime(props.row.created_at) }}
                            </span>
                            <span v-else-if="props.column.field === 'export'">
                                <a
                                    class="btn btn-info btn-sm"
                                    :href="route('admin.pgc-giftcards.export-card-pdf', [seriesSlug, props.row.id])"
                                >
                                    Export to PDF
                                </a>
                            </span>
                            <table-row-actions v-else-if="props.column.field === 'action'">
                                <a
                                    href="#"
                                    class="dropdown-item"
                                    :class="{
                                        'text-danger': props.row.is_active,
                                        disabled: togglingCardId === props.row.id
                                    }"
                                    @click.prevent="togglingCardId === props.row.id ? null : toggleCardStatus(props.row)"
                                >
                                    {{ props.row.is_active ? 'Deactivate' : 'Activate' }}
                                    <i v-if="togglingCardId === props.row.id" class="fa fa-spinner fa-spin"></i>
                                </a>
                                <a
                                    v-if="!props.row.fully_used_at"
                                    href="#"
                                    class="dropdown-item"
                                    :class="{ disabled: markingCardId === props.row.id }"
                                    @click.prevent="markingCardId === props.row.id ? null : markCardUsed(props.row)"
                                >
                                    Mark as Used
                                    <i v-if="markingCardId === props.row.id" class="fa fa-spinner fa-spin"></i>
                                </a>
                            </table-row-actions>
                        </template>
                    </vue-good-table>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import PaginationMixin from '../../Shared/Mixins/PaginationMixin';
import DeleteAlert from '../../Shared/Mixins/DeleteAlert';
import { formatDateTime, formatExpiryDateTime } from '../../utils';

export default {
    layout: Layout,
    mixins: [PaginationMixin, DeleteAlert],
    props: ['seriesName', 'seriesSlug', 'giftCards', 'markUsedRoute'],
    data() {
        return {
            search: '',
            marking: false,
            markingCardId: null,
            togglingCardId: null,
            exportCards: [],
            allGiftCards: [],
            totalRecords: 0,
            serverParams: {
                page: 1,
                perPage: 10,
            },
            paginationOptions: {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                perPageDropdown: [5, 10, 20, 50, 100, 200],
                dropdownAllowAll: false,
            },
            sortingOptions: {
                enabled: false,
            },
            columns: [
                { label: 'S/N', field: 'sn', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Series', field: 'series_name', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Code', field: 'code', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Value', field: 'amount', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Used', field: 'used_amount', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Remaining', field: 'remaining', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Usage', field: 'status', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Status', field: 'is_active', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Expires', field: 'expires_at', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Export Gift Card', field: 'export', sortable: false, thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Action', field: 'action', sortable: false, thClass: 'text-center', tdClass: 'text-center' },
            ],
            exportColumns: [
                { label: 'Id', field: 'id' },
                { label: 'Gift Card name', field: 'series_name' },
                { label: 'Gift Card code', field: 'code' },
                { label: 'Value', field: 'value' },
                { label: 'Used by', field: 'used_by' },
                { label: 'Expiry Date', field: 'expires_at' },
                { label: 'Status', field: 'status' },
            ],
            tableDataLoading: false,
        };
    },
    computed: {
        exportFields() {
            const fields = {};
            this.exportColumns.forEach((column) => {
                fields[column.label] = column.field;
            });

            return fields;
        },
    },
    mounted() {
        this.allGiftCards = this.giftCards?.data ?? [];
        this.totalRecords = this.giftCards?.total ?? 0;
        this.loadExportCards();
    },
    methods: {
        formatDateTime,
        formatExpiryDateTime,
        remaining(card) {
            return Math.max(Number(card.amount) - Number(card.used_amount), 0);
        },
        async loadExportCards() {
            try {
                const { data } = await axios.get(route('admin.pgc-giftcards.export-data', this.seriesSlug));
                this.exportCards = data;
            } catch (e) {
                console.log(e);
            }
        },
        exportData(fileType) {
            if (!this.exportCards.length) {
                this.$page.props.flash.error = 'No gift cards available to export.';
                return;
            }

            const refName = fileType === 'csv' ? 'exporter-csv' : 'exporter-xls';

            this.$nextTick(() => {
                this.$refs[refName].generate();
            });
        },
        applySearch() {
            this.updateParams({ page: 1 });
            this.paginatedData();
        },
        resetFilters() {
            this.search = '';
            this.updateParams({ page: 1 });
            this.paginatedData();
        },
        async paginatedData() {
            try {
                this.tableDataLoading = true;

                const response = await axios.get(route('admin.pgc-giftcards.show', this.seriesSlug), {
                    params: {
                        page: this.serverParams.page,
                        per_page: this.serverParams.perPage,
                        search: this.search || undefined,
                        api: true,
                    },
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                this.totalRecords = response.data.total;
                this.paginationOptions.perPage = response.data.per_page;
                this.serverParams.perPage = response.data.per_page;
                this.paginationOptions.setCurrentPage = response.data.current_page;
                this.allGiftCards = response.data.data;
            } catch (e) {
                console.log(e);
            } finally {
                this.tableDataLoading = false;
            }
        },
        async markSeriesUsed() {
            const confirmed = await this.deleteAlert(
                'Mark all cards in this series as fully used?',
                "You won't be able to revert this!",
                true,
                '#3085d6',
                '#d33',
                'Yes, mark as used',
            );

            if (!confirmed) {
                return;
            }

            this.marking = true;
            this.$inertia.post(this.markUsedRoute, {}, {
                preserveScroll: true,
                onSuccess: () => {
                    this.paginatedData();
                    this.loadExportCards();
                },
                onFinish: () => {
                    this.marking = false;
                },
            });
        },
        async markCardUsed(card) {
            const confirmed = await this.deleteAlert(
                `Mark gift card ${card.code} as fully used?`,
                "You won't be able to revert this!",
                true,
                '#3085d6',
                '#d33',
                'Yes, mark as used',
            );

            if (!confirmed) {
                return;
            }

            this.markingCardId = card.id;
            this.$inertia.post(route('admin.pgc-giftcards.mark-card-used', [this.seriesSlug, card.id]), {}, {
                preserveScroll: true,
                onSuccess: () => {
                    this.paginatedData();
                    this.loadExportCards();
                },
                onFinish: () => {
                    this.markingCardId = null;
                },
            });
        },
        async toggleCardStatus(card) {
            const activating = !card.is_active;

            const confirmed = await this.deleteAlert(
                `${activating ? 'Activate' : 'Deactivate'} gift card ${card.code}?`,
                "You won't be able to revert this!",
                true,
                '#3085d6',
                '#d33',
                activating ? 'Yes, activate' : 'Yes, deactivate',
            );

            if (!confirmed) {
                return;
            }

            this.togglingCardId = card.id;
            this.$inertia.post(route('admin.pgc-giftcards.update-card-status', [this.seriesSlug, card.id]), {
                is_active: activating,
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    this.paginatedData();
                    this.loadExportCards();
                },
                onFinish: () => {
                    this.togglingCardId = null;
                },
            });
        },
    },
};
</script>

<style scoped>
.export-link {
    font-size: 14px;
}
</style>
